/**
 * Centralized configuration utility
 * Provides easy access to WordPress settings from window.ai360smartsearchSettings
 */

/**
 * Get configuration value from WordPress settings
 * Supports nested paths like 'results.minResults'
 * 
 * @param key - The configuration key (supports dot notation for nested paths)
 * @param defaultValue - Default value if key is not found
 * @returns The configuration value or default
 */
export function getConfig<T>(key: string, defaultValue: T): T {
  const wpSettings = (window as any).ai360smartsearchSettings || {};
  
  // Search in root first
  if (wpSettings[key] !== undefined) {
    return wpSettings[key];
  }
  
  // Search in nested objects (results.xxx, userControls.xxx)
  const parts = key.split('.');
  let value: any = wpSettings;
  
  for (const part of parts) {
    if (value && typeof value === 'object' && part in value) {
      value = value[part];
    } else {
      return defaultValue;
    }
  }
  
  return value !== undefined ? value : defaultValue;
}

/**
 * Get all configuration
 * @returns Complete configuration object
 */
export function getAllConfig(): Record<string, any> {
  return (window as any).ai360smartsearchSettings || {};
}

/**
 * Check if configuration key exists
 * @param key - The configuration key
 * @returns True if key exists
 */
export function hasConfig(key: string): boolean {
  const wpSettings = (window as any).ai360smartsearchSettings || {};
  
  if (wpSettings[key] !== undefined) {
    return true;
  }
  
  const parts = key.split('.');
  let value: any = wpSettings;
  
  for (const part of parts) {
    if (value && typeof value === 'object' && part in value) {
      value = value[part];
    } else {
      return false;
    }
  }
  
  return value !== undefined;
}
